import React, { useEffect, useState, useRef } from 'react'
import { Tree as ArboristTree, NodeApi, TreeApi } from 'react-arborist'
import { Stack } from '@mui/material'

import { Device, TreeNode as TreeNodeType, Map } from '../types'
import { useAccessPointZonesContext } from '../AccessPointZonesContext'
import DisableMap from './DisableMap'
import DisableDevice from './DisableDevice'
import SidebarLoader from './SidebarLoader'
import Node from './Node'

const useResize = ({ ref }: { ref: React.RefObject<HTMLDivElement> }) => {
  const [height, setHeight] = useState<number>(400)
  useEffect(() => {
    if (!ref.current) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height
        if (height > 0) {
          setHeight(height)
        }
      }
    })

    resizeObserver.observe(ref.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])
  return height
}

const Tree = () => {
  const { tree, initialOpenState, onMapUuidChange } = useAccessPointZonesContext()
  const containerRef = useRef<HTMLDivElement>(null)
  const treeRef = useRef<TreeApi<TreeNodeType> | null>(null)
  const height = useResize({ ref: containerRef })
  const [mapToDisabled, setMapToDisabled] = useState<Map | null>(null)
  const [deviceToDisabled, setDeviceToDisabled] = useState<Device | null>(null)

  const openNodes = (node: NodeApi<TreeNodeType>) => {
    const ancestorIds: string[] = []
    let current: NodeApi<TreeNodeType> | null = node
    while (current) {
      ancestorIds.unshift(current.id)
      current = current.parent
    }

    treeRef.current?.closeAll()
    ancestorIds.forEach(id => treeRef.current?.open(id))
    if (node.data.data.type === 'map') {
      onMapUuidChange(node.data.id)
    }
  }

  const handleRowClick = (node: NodeApi<TreeNodeType>) => {
    if (node.level === 0) {
      return
    } else if (['empty', 'device'].includes(node.data.data.type)) {
      return
    } else if (node.data.data.type === 'surface_area' || node.data.data.type === 'map') {
      if (node.isOpen) {
        node.close()
      } else {
        openNodes(node)
      }
    }
  }

  return (
    <>
      <Stack
        ref={containerRef}
        gap={1}
        sx={{ width: 280, flexShrink: 0, height: '100%' }}>
        <ArboristTree<TreeNodeType>
          ref={treeRef}
          data={tree}
          openByDefault={false}
          initialOpenState={initialOpenState}
          width={'100%'}
          height={height}
          rowHeight={26}
        >
          {nodeProps => (
            <Node
              {...nodeProps}
              onClick={handleRowClick}
              onMapDisable={setMapToDisabled}
              onDeviceDisable={setDeviceToDisabled}
            />
          )}
        </ArboristTree>
      </Stack>
      {
        Boolean(mapToDisabled) && (
          <DisableMap onClose={() => setMapToDisabled(null)} map={mapToDisabled} />
        )
      }
      {
        Boolean(deviceToDisabled) && (
          <DisableDevice onClose={() => setDeviceToDisabled(null)} device={deviceToDisabled as Device} source="tree" />
        )
      }
    </>
  )
}

const Loader = () => {
  const { loading, mapUuid } = useAccessPointZonesContext()

  if (loading || !mapUuid) {
    return <SidebarLoader />
  }

  return (
    <Tree />
  )
}
export default Loader